import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import i18n from "i18next";
import { GUIDES, groupByFeature, searchGuides, resolveGuides } from "./guides/index";
import formatText from "./formatText";
import { getToken } from "../../../common/environment_config.es6";
import "./HelpHub.scss";

// Promotion-video placement for the Help Hub. Admins upload per-section clips on
// /admin/promotion-videos under this location, keyed `<guideId>:<sectionId>`.
const HELP_HUB_LOCATION = "help-hub";

// Self-contained Help Hub popup. Opens on the window event `aidea:openHelpHub`
// (dispatched by the nav-bar Help button), so no prop wiring is needed. Content
// is the in-code guide registry: feature-grouped directory + global search on
// the left, and the selected guide (index + per-section video + text) on the right.
export default function HelpHub() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(GUIDES[0] ? GUIDES[0].id : null);
  // refKey (`<guideId>:<sectionId>`) → admin-uploaded video URL.
  const [videosByKey, setVideosByKey] = useState({});
  const sectionRefs = useRef({});
  const contentRef = useRef(null);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("aidea:openHelpHub", onOpen);
    return () => window.removeEventListener("aidea:openHelpHub", onOpen);
  }, []);

  // Load admin-managed promotion videos once the hub is opened. Any section
  // whose refKey matches fills its slot; the rest keep their in-code videoUrl
  // (or the "coming soon" placeholder). Failures are silent — help still works.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const apiUrl = process.env.MW_API_URL || window.ideaLibraryRootUrl || "";
    (async () => {
      try {
        const res = await fetch(`${apiUrl}api/promotion-videos?location=${HELP_HUB_LOCATION}`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (!res.ok) return;
        const rows = await res.json();
        if (cancelled || !Array.isArray(rows)) return;
        // Rows arrive newest-first and may carry a per-locale variant. Per refKey
        // pick: editor-language row > locale-less row > any row; first (newest)
        // wins within a tier.
        const uiLang = ((i18n.language || "en") + "").slice(0, 2);
        const rank = (r) => (r.locale && r.locale.slice(0, 2) === uiLang ? 0 : r.locale ? 2 : 1);
        const map = {};
        const best = {};
        for (const r of rows) {
          if (!r || !r.refKey || !r.videoUrl) continue;
          const score = rank(r);
          if (best[r.refKey] === undefined || score < best[r.refKey]) {
            best[r.refKey] = score;
            map[r.refKey] = r.videoUrl;
          }
        }
        setVideosByKey(map);
      } catch (err) {
        console.error("[HelpHub] Failed to load promotion videos:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Resolve guides to the editor's current language (falls back to English).
  const lang = ((i18n.language || i18n.resolvedLanguage || "en") + "").slice(0, 2);
  const resolved = useMemo(() => resolveGuides(lang), [lang]);
  const results = useMemo(() => searchGuides(query, resolved), [query, resolved]);
  const groups = useMemo(() => groupByFeature(results), [results]);

  // Keep a valid selection: prefer the current one if still in results, else the
  // first result (so searching auto-focuses the top hit).
  const selected = results.find((g) => g.id === selectedId) || results[0] || null;
  const selectedKey = selected ? selected.id : null;

  // A new guide starts at its top — otherwise the content pane keeps the previous
  // guide's scroll offset and the switch lands mid-page.
  useEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [selectedKey]);

  // i18next echoes the key on a miss, so `t(key) || fallback` fails for
  // not-yet-translated keys. Use defaultValue (i18next's real fallback), and
  // keep `|| fallback` as a belt for test mocks that return "".
  const t = (key, fallback) => i18n.t(key, { defaultValue: fallback }) || fallback;

  const scrollToSection = useCallback((sid) => {
    const el = sectionRefs.current[sid];
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  if (!open) return null;

  return (
    <div
      className="help-hub__backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) setOpen(false);
      }}
    >
      <div className="help-hub" role="dialog" aria-label={t("help.title", "Help")}>
        <div className="help-hub__header">
          <div className="help-hub__brand">
            <i className="material-icons">help_outline</i>
            <span>{t("help.title", "Help & guides")}</span>
          </div>
          <input
            className="help-hub__search"
            type="text"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("help.search_placeholder", "Search features…")}
            aria-label={t("help.search_placeholder", "Search features…")}
          />
          <button
            type="button"
            className="help-hub__close"
            onClick={() => setOpen(false)}
            aria-label={t("close", "Close")}
          >
            ×
          </button>
        </div>

        <div className="help-hub__body">
          <nav className="help-hub__rail" aria-label={t("help.title", "Help")}>
            {groups.length === 0 && (
              <div className="help-hub__empty">{t("help.no_results", "No guides match.")}</div>
            )}
            {groups.map((grp) => (
              <div key={grp.feature} className="help-hub__group">
                <div className="help-hub__group-title">{grp.feature}</div>
                {grp.guides.map((g) => (
                  <button
                    type="button"
                    key={g.id}
                    className={
                      "help-hub__guide-item" +
                      (selected && selected.id === g.id ? " is-active" : "")
                    }
                    onClick={() => setSelectedId(g.id)}
                  >
                    {g.icon && <i className="material-icons">{g.icon}</i>}
                    <span>{g.title}</span>
                  </button>
                ))}
              </div>
            ))}
          </nav>

          <div className="help-hub__content" ref={contentRef}>
            {!selected && (
              <div className="help-hub__empty">{t("help.no_results", "No guides match.")}</div>
            )}
            {selected && (
              // Keyed so each guide switch remounts and replays the fade-in.
              <div key={selected.id} className="help-hub__page">
                <h2 className="help-hub__guide-title">{selected.title}</h2>
                <ol className="help-hub__index">
                  {selected.sections.map((s) => (
                    <li key={s.id}>
                      <button type="button" onClick={() => scrollToSection(s.id)}>
                        {s.title}
                      </button>
                    </li>
                  ))}
                </ol>
                <div className="help-hub__sections">
                  {selected.sections.map((s) => {
                    // Admin-uploaded clip (by refKey) wins; else the in-code URL.
                    const videoUrl = videosByKey[`${selected.id}:${s.id}`] || s.videoUrl;
                    return (
                      <section
                        key={s.id}
                        className="help-hub__section"
                        ref={(el) => {
                          sectionRefs.current[s.id] = el;
                        }}
                      >
                        <h3 className="help-hub__section-title">{s.title}</h3>
                        <div className="help-hub__video">
                          {videoUrl ? (
                            <video
                              controls
                              controlsList="nodownload noplaybackrate noremoteplayback"
                              disablePictureInPicture
                              preload="metadata"
                              src={videoUrl}
                            />
                          ) : (
                            <div className="help-hub__video-placeholder">
                              <i className="material-icons">movie</i>
                              <span>{t("help.video_soon", "Video coming soon")}</span>
                            </div>
                          )}
                        </div>
                        <div className="help-hub__text">{formatText(s.text)}</div>
                      </section>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
